// pages/recommendations.js — the unobtrusive "Suggested actions" strip on the
// Protection page.
//
// It detects REAL problematic patterns from the events the dashboard already
// has (state.events, ~200 rows from /v1/teams/events) and, when a pattern
// crosses its threshold, surfaces ONE card per pattern recommending a concrete
// action: either flip a real team setting (via applySettingValue) or open the
// policy builder pre-filled with a starting rule (openPolicyBuilderForSuggestedRule).
//
// Design constraints (from the user):
//   - Threshold-gated: a card only appears once a pattern is actually
//     ESTABLISHED (e.g. branch contended ≥3× in 7d). A healthy/quiet team sees
//     NOTHING — the strip renders empty, zero chrome.
//   - Dismissible: each card has an ✕ that hides it forever (per team, via
//     localStorage). A footer link disables the whole strip; it's re-enableable.
//   - Honest: counts come straight from the events; a "turn off" suggestion only
//     shows when the setting is actually on. No fabricated numbers.
//
// Every pattern below is backed by an event type confirmed emitted server-side
// and an action that really takes effect. See the plan doc for the mapping.

import { state } from "../state.js";
import { escapeHTML } from "../util.js";
import { isFlaggedEvent } from "../format.js";
import { showToast, showConfirm } from "../ui.js";
import {
  applySettingValue,
  currentSettingValue,
  hasTeamSettings,
  fetchTeamSettings,
} from "../team-settings.js";
import { openPolicyBuilderForSuggestedRule } from "../policy-builder.js";

// ---- dismissal (mirrors banners.js: localStorage keyed by team_id) ----

function teamId() {
  return (state.teamInfo && state.teamInfo.team_id) || "local";
}
function recoDismissKey(id) {
  return "ac-reco-" + id + ":" + teamId();
}
function recosDisabledKey() {
  return "ac-recos-disabled:" + teamId();
}
function isRecoDismissed(id) {
  try {
    return localStorage.getItem(recoDismissKey(id)) === "1";
  } catch {
    return false;
  }
}
function dismissReco(id) {
  try {
    localStorage.setItem(recoDismissKey(id), "1");
  } catch {
    /* private mode — dismissal just won't persist */
  }
}
function recosDisabled() {
  try {
    return localStorage.getItem(recosDisabledKey()) === "1";
  } catch {
    return false;
  }
}
function setRecosDisabled(off) {
  try {
    if (off) localStorage.setItem(recosDisabledKey(), "1");
    else localStorage.removeItem(recosDisabledKey());
  } catch {
    /* best-effort */
  }
}

// ---- pattern detection (client-side over state.events) ----

const ev = (e) => e.EventType || e.event_type || "";
const payloadOf = (e) => e.Payload || e.payload || {};
const occurredMs = (e) => {
  const t = e.OccurredAt || e.occurred_at || "";
  const ms = t ? Date.parse(t) : NaN;
  return Number.isNaN(ms) ? null : ms;
};

// withinWindow filters events to those whose occurred_at is within `hours` of
// now. Events with an unparseable timestamp are EXCLUDED (we never inflate a
// count off a bad date) — but if a MEANINGFUL fraction is unparseable, the
// detectors would silently under-count and never fire. Warn once per session so
// a systemic occurred_at format break is diagnosable instead of invisible.
let _warnedBadTimestamps = false;
function withinWindow(events, hours) {
  const cutoff = Date.now() - hours * 3600 * 1000;
  let dropped = 0;
  const kept = events.filter((e) => {
    const ms = occurredMs(e);
    if (ms === null) {
      dropped++;
      return false;
    }
    return ms >= cutoff;
  });
  if (
    !_warnedBadTimestamps &&
    events.length >= 20 &&
    dropped / events.length > 0.05
  ) {
    _warnedBadTimestamps = true;
    // eslint-disable-next-line no-console
    console.warn(
      `[recommendations] ${dropped}/${events.length} events have an unparseable occurred_at — ` +
        `pattern detection is under-counting. Check the event timestamp format.`,
    );
  }
  return kept;
}

// detectPatterns returns an array of recommendation objects for patterns that
// cross threshold. Each: { id, title, detail, action }. Pure over state.events;
// returns [] when nothing qualifies (so the strip stays empty).
function detectPatterns() {
  const events = Array.isArray(state.events) ? state.events : [];
  if (events.length === 0) return [];
  const recos = [];

  // NOTE on the HMAC key (hosted mode): event payloads arrive AES-encrypted and
  // are only decrypted into e.payload when the team HMAC key is loaded in the
  // browser (the same key the Events page needs to show real file paths — the
  // "Import from Local Daemon" flow on /machines). Patterns that read a decrypted
  // payload field (branch on #1, reason=heartbeat_stale on #2) simply find
  // nothing when the key is absent — they degrade to "no card" rather than
  // firing on bad data. Count-only patterns (#3/#4/#5) don't need the payload
  // and work regardless. So a keyless dashboard shows fewer recos, never wrong ones.

  // 1. Branch contention — ≥3 branch.contended on the SAME branch in 7d.
  //    Action: suggest a require_confirm rule scoped to that branch.
  {
    const week = withinWindow(events, 168);
    const byBranch = new Map();
    for (const e of week) {
      if (ev(e) !== "branch.contended") continue;
      const b = payloadOf(e).branch || payloadOf(e).Branch || "";
      if (!b) continue;
      byBranch.set(b, (byBranch.get(b) || 0) + 1);
    }
    let topBranch = "";
    let topCount = 0;
    for (const [b, n] of byBranch) {
      if (n > topCount) {
        topCount = n;
        topBranch = b;
      }
    }
    if (topCount >= 3) {
      recos.push({
        id: "branch-contention",
        title: `"${topBranch}" contended ${topCount}× this week`,
        detail:
          "Multiple chats keep landing on the same branch. A rule can ask agents to confirm before working it, so they coordinate instead of colliding.",
        action: {
          kind: "rule",
          label: "Suggest a rule",
          draft: {
            name: "confirm-on-" + slug(topBranch),
            reason:
              "Branch '" +
              topBranch +
              "' saw repeated contention — confirm before editing so chats coordinate.",
            decision: "require_confirm",
            mode: "enforce",
            grace_seconds: 600,
            constraint: { Resource: { Branch: topBranch, Branches: [topBranch] } },
          },
          subtitle:
            "Suggested from " + topCount + " contentions on '" + topBranch + "'.",
        },
      });
    }
  }

  // 2. Heartbeat-stale deaths — ≥2 lease.expired (reason heartbeat_stale) on the
  //    SAME agent in 24h. Action: lower orphan_threshold_secs so stale leases
  //    reclaim faster. Only if it's not already low.
  {
    const day = withinWindow(events, 24);
    const byAgent = new Map();
    for (const e of day) {
      if (ev(e) !== "lease.expired") continue;
      if (payloadOf(e).reason !== "heartbeat_stale") continue;
      const a = e.AgentID || e.agent_id || "";
      if (!a) continue;
      byAgent.set(a, (byAgent.get(a) || 0) + 1);
    }
    let worst = 0;
    for (const n of byAgent.values()) if (n > worst) worst = n;
    // Fail-SAFE: require a KNOWN current value. numOr(..., 120) would default
    // to 120 when settings haven't loaded and wrongly suggest "lower to 60s"
    // even if the real value is already 60. Only suggest when we can read the
    // live value AND it's actually above 60.
    const rawOrphan = currentSettingValue("orphan_threshold_secs");
    const cur = numOr(rawOrphan, 0);
    if (worst >= 2 && cur > 60) {
      recos.push({
        id: "heartbeat-stale",
        title: `An agent went silent mid-edit ${worst}× today`,
        detail:
          "Chats are dying while holding a file, leaving it locked until the orphan timer fires (currently " +
          cur +
          "s). Lowering it to 60s reclaims abandoned files faster.",
        action: {
          kind: "setting",
          label: "Lower orphan threshold to 60s",
          key: "orphan_threshold_secs",
          value: 60,
          confirm:
            "Reclaim orphaned leases after 60s instead of " + cur + "s?",
        },
      });
    }
  }

  // 3. Unmanaged writes — ≥5 write/delete.unmanaged_conflict in 7d. Action: turn
  //    ON watch_unmanaged_writes_enabled (detection/audit) if it's off.
  {
    const week = withinWindow(events, 168);
    let n = 0;
    for (const e of week) {
      const t = ev(e);
      if (
        (t === "write.unmanaged_conflict" || t === "delete.unmanaged_conflict") &&
        isFlaggedEvent(t, e.Payload || e.payload)
      )
        n++;
    }
    // Fail-SAFE: only suggest "turn on" when the setting is CONFIRMED off
    // (=== false). If settings haven't loaded yet, currentSettingValue returns
    // undefined — suggesting "turn on" then is wrong (and contradictory here:
    // these very events are EMITTED by the detection we'd be telling them to
    // enable, so it's provably already on). Treat unknown as "don't suggest".
    const off = currentSettingValue("watch_unmanaged_writes_enabled") === false;
    if (n >= 5 && off) {
      recos.push({
        id: "unmanaged-writes",
        title: `${n} writes landed on leased files this week`,
        detail:
          "Edits are happening outside the holding agent. Turn on unmanaged-write detection to record an audit event each time, so you can see who's bypassing coordination.",
        action: {
          kind: "setting",
          label: "Turn on write detection",
          key: "watch_unmanaged_writes_enabled",
          value: true,
          confirm: "Enable detection of writes to leased files?",
        },
      });
    }
  }

  // 4. Preemptions — ≥2 lease.preempted in 24h. Action: enable autonomy auto so
  //    agents negotiate handoffs instead of force-preempting. Only if not auto.
  {
    const day = withinWindow(events, 24);
    let n = 0;
    for (const e of day) if (ev(e) === "lease.preempted") n++;
    const auton = currentSettingValue("autonomy_level");
    // ≥3 (not ≥2) per 24h: 2 preemptions can be normal priority-driven churn on
    // a busy day; 3 is a stronger "this keeps happening" signal worth a nudge,
    // and keeps the per-window density in line with the branch-contention bar.
    // Fail-SAFE: require a KNOWN value that isn't already "auto" — undefined
    // (settings not loaded) must NOT fire (undefined !== "auto" is truthy).
    if (n >= 3 && auton != null && auton !== "auto") {
      recos.push({
        id: "preemptions",
        title: `${n} forced preemptions in the last 24h`,
        detail:
          "Higher-priority chats are evicting holders mid-work. Autonomy 'auto' lets agents reach out and hand files off cleanly instead of yanking them.",
        action: {
          kind: "setting",
          label: "Turn on autonomy: auto",
          key: "autonomy_level",
          value: "auto",
          confirm: "Let agents negotiate and hand off files on their own (autonomy: auto)?",
        },
      });
    }
  }

  // 5. Commit bypass — ≥1 commit.bypass_detected in 7d. Action: auto-install the
  //    pre-commit hook so bypassing commits are actually blocked. Only if off.
  {
    const week = withinWindow(events, 168);
    let n = 0;
    for (const e of week) if (ev(e) === "commit.bypass_detected") n++;
    // Fail-SAFE (see unmanaged-writes above): only suggest "turn on" when the
    // setting is CONFIRMED off; unknown (settings not loaded) → don't suggest.
    const off = currentSettingValue("auto_install_pre_commit_hook") === false;
    if (n >= 1 && off) {
      recos.push({
        id: "commit-bypass",
        title:
          n === 1
            ? "A commit bypassed coordination this week"
            : `${n} commits bypassed coordination this week`,
        detail:
          "A commit touched a file another chat was holding, outside coordination. Auto-installing the pre-commit hook blocks these before they land.",
        action: {
          kind: "setting",
          label: "Auto-install pre-commit hook",
          key: "auto_install_pre_commit_hook",
          value: true,
          confirm:
            "Install AgentCollision's pre-commit hook in managed repos to block bypassing commits?",
        },
      });
    }
  }

  return recos.filter((r) => !isRecoDismissed(r.id));
}

function slug(s) {
  return String(s || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "")
    .slice(0, 40) || "branch";
}
function numOr(v, fallback) {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

// ---- render: bell + slide-in drawer ----

let bound = false;
// _liveRecos holds the currently-detected recommendations so handlers resolve an
// action by id without re-deriving it. Re-set on every render.
let _liveRecos = [];

// Drawer open/close manage two pending timers so rapid toggles don't fight:
//  - openFrame: the rAF that adds .open (cancelled if we close before it fires)
//  - closeTimer: the setTimeout that hides after the slide-out (cancelled if we
//    re-open before it fires, so a stale timer can't hide a freshly-opened drawer)
let openFrame = null;
let closeTimer = null;

// cleanupDrawerAnimation cancels any pending open-frame / close-timer. Called
// from the Protection page's stop() so leaving the page doesn't leave a timer
// armed to fire a hide on already-hidden (or since-rerendered) elements.
export function cleanupDrawerAnimation() {
  if (openFrame !== null) { cancelAnimationFrame(openFrame); openFrame = null; }
  if (closeTimer !== null) { clearTimeout(closeTimer); closeTimer = null; }
}
function openDrawer() {
  const d = document.getElementById("reco-drawer");
  const b = document.getElementById("reco-backdrop");
  if (!d || !b) return;
  if (closeTimer !== null) { clearTimeout(closeTimer); closeTimer = null; }
  b.hidden = false;
  d.hidden = false;
  // next frame → add the .open class so the CSS transition runs (slide + fade).
  if (openFrame !== null) cancelAnimationFrame(openFrame);
  openFrame = requestAnimationFrame(() => {
    openFrame = null;
    b.classList.add("open");
    d.classList.add("open");
  });
}
function closeDrawer() {
  const d = document.getElementById("reco-drawer");
  const b = document.getElementById("reco-backdrop");
  if (!d || !b) return;
  if (openFrame !== null) { cancelAnimationFrame(openFrame); openFrame = null; }
  b.classList.remove("open");
  d.classList.remove("open");
  // Return focus to the bell that opened the drawer (a11y: focus must not be
  // lost to document.body when the drawer is hidden).
  const bell = document.getElementById("reco-bell");
  if (bell && !bell.hidden) bell.focus();
  // Hide after the transition so it's not focusable while off-screen. Both the
  // backdrop and drawer transitions are 0.26s, so this fires once both finish.
  if (closeTimer !== null) clearTimeout(closeTimer);
  closeTimer = setTimeout(() => {
    closeTimer = null;
    b.hidden = true;
    d.hidden = true;
  }, 260);
}

// renderRecommendations updates the bell (count + dot + visibility) and fills the
// drawer body. It NEVER renders inline on the page — the strip lives behind the
// bell so it can't cover the Protection KPIs. When recos are globally disabled or
// no pattern qualifies, the bell hides entirely (zero chrome).
// _settingsRequested guards the lazy settings fetch so we don't refetch on
// every render tick — only once until it lands (then renderRecommendations
// re-runs with real data).
let _settingsRequested = false;
export function renderRecommendations() {
  bindOnce();
  const bell = document.getElementById("reco-bell");
  const badge = document.getElementById("reco-bell-badge");
  const body = document.getElementById("reco-drawer-body");
  if (!bell || !body) return;

  // The settings-based recos (write detection, pre-commit hook, orphan
  // threshold, autonomy) gate on the LIVE setting value. The Protection page
  // doesn't otherwise load team settings, so on a cold mount teamState.settings
  // is empty and those gates would read undefined. The gates now fail SAFE
  // (won't suggest "turn on" without a confirmed-off value), but we also lazily
  // load settings here so a genuinely-off setting still surfaces its reco. Fetch
  // once, then re-render when it resolves.
  if (!hasTeamSettings() && !_settingsRequested) {
    _settingsRequested = true;
    // NOTE: fetchTeamSettings() swallows its own errors and NEVER rejects, so a
    // .catch here would be dead code. Detect a failed load by checking
    // hasTeamSettings() AFTER it resolves: if settings still aren't present the
    // fetch failed (e.g. the coordinator circuit breaker was open during a
    // rate-limit, or a transient 401). Reset the guard so a later render tick
    // (~15s/60s dashboard:tick) retries — otherwise the settings-recos would
    // stay suppressed for the whole session until a full page reload. Return
    // early (don't recurse) so a persistently-open breaker can't tight-loop;
    // the next natural tick carries the retry. The fail-safe gates keep the
    // recos correct (no false "turn on X") while settings are still missing.
    fetchTeamSettings().then(() => {
      if (!hasTeamSettings()) {
        _settingsRequested = false;
        return;
      }
      renderRecommendations();
    });
  }

  let recos = [];
  try {
    recos = detectPatterns();
  } catch {
    recos = []; // detection must never break the page
  }
  _liveRecos = recos;

  // Globally disabled but there ARE pending patterns: keep a MUTED bell (no
  // badge, no dot — just a quiet icon) as the recoverable way back in. Clicking
  // it opens the drawer showing a "Show recommendations" re-enable. If there are
  // no patterns at all, hide the bell entirely (zero chrome).
  if (recosDisabled()) {
    if (recos.length === 0) {
      bell.hidden = true;
      closeDrawer();
    } else {
      bell.hidden = false;
      bell.classList.remove("has-recos"); // muted: no dot/badge
      if (badge) badge.textContent = "";
      body.innerHTML =
        `<div class="reco-empty">Recommendations are turned off.</div>
         <button type="button" class="reco-apply" data-reco-reenable>Show recommendations</button>`;
    }
    return;
  }

  if (recos.length === 0) {
    bell.hidden = true;
    bell.classList.remove("has-recos");
    closeDrawer();
    return;
  }

  // Bell visible with a count badge + the accent dot.
  bell.hidden = false;
  bell.classList.add("has-recos");
  if (badge) badge.textContent = String(recos.length);

  body.innerHTML = recos
    .map(
      (r) => `
    <div class="reco-card" data-reco-id="${escapeHTML(r.id)}">
      <button type="button" class="reco-dismiss" data-reco-dismiss="${escapeHTML(r.id)}" title="Dismiss" aria-label="Dismiss recommendation">&times;</button>
      <div class="reco-title">${escapeHTML(r.title)}</div>
      <div class="reco-detail">${escapeHTML(r.detail)}</div>
      <button type="button" class="reco-apply" data-reco-action="${escapeHTML(r.id)}">${escapeHTML(r.action.label)}</button>
    </div>`,
    )
    .join("");
}

function bindOnce() {
  if (bound) return;
  bound = true;
  // Bell toggles the drawer.
  document.addEventListener("click", (e) => {
    if (e.target.closest("#reco-bell")) {
      const d = document.getElementById("reco-drawer");
      if (d && d.classList.contains("open")) closeDrawer();
      else openDrawer();
    }
    if (e.target.closest("#reco-drawer-close") || e.target.closest("#reco-backdrop")) {
      closeDrawer();
    }
  });
  // Esc closes the drawer.
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape") closeDrawer();
  });
  // Delegated card actions (dismiss / disable / apply). Off document so it
  // survives the drawer-body innerHTML swaps.
  document.addEventListener("click", async (e) => {
    const disable = e.target.closest("[data-reco-disable]");
    if (disable) {
      setRecosDisabled(true);
      renderRecommendations();
      return;
    }
    const reEnable = e.target.closest("[data-reco-reenable]");
    if (reEnable) {
      setRecosDisabled(false);
      renderRecommendations();
      openDrawer(); // show the freshly-restored recos right away
      return;
    }
    const dismiss = e.target.closest("[data-reco-dismiss]");
    if (dismiss) {
      dismissReco(dismiss.getAttribute("data-reco-dismiss"));
      renderRecommendations();
      return;
    }
    const apply = e.target.closest("[data-reco-action]");
    if (apply) {
      const id = apply.getAttribute("data-reco-action");
      const reco = _liveRecos.find((r) => r.id === id);
      if (reco) await runAction(reco, apply);
      return;
    }
  });
}

async function runAction(reco, btn) {
  const a = reco.action;
  if (a.kind === "rule") {
    // Pre-fill the policy builder; the user reviews + saves there.
    openPolicyBuilderForSuggestedRule(a.draft, a.subtitle);
    return;
  }
  if (a.kind === "setting") {
    const ok = await showConfirm(
      "Apply this setting?",
      escapeHTML(a.confirm || "Apply the recommended setting?"),
      "Apply",
    );
    if (!ok) return;
    const prev = btn.textContent;
    btn.disabled = true;
    btn.textContent = "Applying…";
    try {
      await applySettingValue(a.key, a.value);
      showToast("success", "Setting applied. Team daemons sync within ~5s.");
      // The pattern's trigger condition (setting was off / threshold high) no
      // longer holds, so re-render drops the card naturally.
      renderRecommendations();
    } catch (err) {
      btn.disabled = false;
      btn.textContent = prev;
      showToast("error", "Couldn't apply: " + (err && err.message ? err.message : "unknown error"));
    }
  }
}
